import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, num, rupee, pnlClass, istDateTime } from "@/lib/api";

const list = (s) => s.split(",").map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x) && x > 0);
const list0 = (s) => s.split(",").filter((x) => x.trim() !== "").map((x) => Number(x.trim())).filter((x) => !Number.isNaN(x) && x >= 0);
const pairs = (s) => s.split(",").map((x) => x.split("/").map((y) => Number(y.trim()))).filter((p) => p.length === 2 && p.every((y) => y > 0));
const pf = (v) => (v === null || v === undefined ? "—" : v >= 999 ? "∞" : num(v));

export const SweepPanel = ({ range, pricing, disabled, onApply }) => {
  const [g, setG] = useState({ or: "15, 30", ema: "9/21, 20/50", rsi: "55, 60", adx: "0, 20, 25", orw: "0, 0.3", min: 30, threeYear: true });
  const [job, setJob] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const follow = async (start) => {
    let j = start;
    let misses = 0;
    setJob(j);
    while (j.status === "running") {
      await new Promise((ok) => setTimeout(ok, 2000));
      try {
        j = (await api.get(`/backtest/sweep/jobs/${j.id}`)).data;
        misses = 0;
        setJob(j);
      } catch (e) {
        if (++misses >= 4) throw e;
      }
    }
    if (j.status === "error") throw new Error(j.error);
    setRes(j.result);
    const best = j.result.results[0]?.out_of_sample?.profit_factor;
    toast.success(`Sweep complete: ${j.result.combos} combinations ranked · best OOS PF ${best ?? "—"}`);
  };

  useEffect(() => {
    api.get("/backtest/sweep/latest").then((r) => r.data?.results && setRes(r.data));
    api.get("/backtest/sweep/active").then((r) => {
      if (!r.data?.id) return;
      setBusy(true);
      follow(r.data).catch((e) => toast.error(errMsg(e))).finally(() => { setBusy(false); setJob(null); });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const combos = list(g.or).length * pairs(g.ema).length * list(g.rsi).length * list0(g.adx).length * list0(g.orw).length;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/backtest/sweep", {
        ...range, pricing, or_minutes: list(g.or), ema_pairs: pairs(g.ema), rsi_long: list(g.rsi),
        adx_min: list0(g.adx), or_width_min_pct: list0(g.orw), min_oos_trades: Number(g.min), years: g.threeYear ? 3 : null,
      });
      await follow(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setJob(null); }
  };

  return (
    <div className="panel" data-testid="panel-parameter-sweep">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-800 p-4">
        <div className="w-full"><span className="eyebrow">Parameter sweep · ranked by out-of-sample profit factor</span></div>
        <F t="OR minutes"><Input data-testid="input-sweep-or" value={g.or} onChange={(e) => setG({ ...g, or: e.target.value })} /></F>
        <F t="EMA fast/slow pairs"><Input data-testid="input-sweep-ema" value={g.ema} onChange={(e) => setG({ ...g, ema: e.target.value })} /></F>
        <F t="RSI long (short = 100 − x)"><Input data-testid="input-sweep-rsi" value={g.rsi} onChange={(e) => setG({ ...g, rsi: e.target.value })} /></F>
        <F t="ADX min (0 = off)"><Input data-testid="input-sweep-adx" value={g.adx} onChange={(e) => setG({ ...g, adx: e.target.value })} /></F>
        <F t="OR width min % (0 = off)"><Input data-testid="input-sweep-orw" value={g.orw} onChange={(e) => setG({ ...g, orw: e.target.value })} /></F>
        <F t="Min OOS trades" w="w-24"><Input data-testid="input-sweep-min" type="number" value={g.min} onChange={(e) => setG({ ...g, min: e.target.value })} /></F>
        <label className="flex h-8 items-center gap-2 text-xs text-slate-300">
          <input data-testid="checkbox-sweep-3y" type="checkbox" checked={g.threeYear} onChange={(e) => setG({ ...g, threeYear: e.target.checked })} className="accent-indigo-500" />
          Last 3 years
        </label>
        <Button data-testid="button-run-sweep" disabled={busy || disabled || combos < 1 || combos > 300} onClick={run} className="h-8 bg-indigo-500 text-xs text-white hover:bg-indigo-400">
          {busy ? "Sweeping…" : `Run ${combos} combos`}
        </Button>
        {combos > 300 && <span className="text-[11px] text-amber-400">Max 300 combinations</span>}
        {job && (
          <div className="w-full" data-testid="sweep-progress">
            <div className="flex justify-between text-[11px] text-slate-400"><span>{job.stage}</span><span className="num">{job.done}/{job.total}</span></div>
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-slate-800"><div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${(100 * job.done) / (job.total || 1)}%` }} /></div>
          </div>
        )}
      </div>
      {res && (
        <>
          <div className="num px-4 pt-2 text-[11px] text-slate-500" data-testid="sweep-meta">
            {res.from_date} → {res.to_date} · {res.pricing} pricing · source {res.source} · run {istDateTime(res.created_at)}
          </div>
          <div className="max-h-[380px] overflow-auto">
            <table className="dense num w-full" data-testid="table-sweep-results">
              <thead className="sticky top-0 bg-[#0D121F]">
                <tr><th>#</th><th>OR</th><th>EMA</th><th>RSI L/S</th><th>ADX≥</th><th>ORw≥</th><th>IS trades</th><th>IS PF</th><th>OOS trades</th><th>OOS PF</th><th>OOS win %</th><th>OOS net</th><th>OOS max DD</th><th></th></tr>
              </thead>
              <tbody>
                {res.results.map((x) => {
                  const o = x.out_of_sample;
                  return (
                    <tr key={x.rank} data-testid={`sweep-row-${x.rank}`} className={x.eligible ? "" : "opacity-50"}>
                      <td className="text-slate-500">{x.rank}</td><td>{x.or_minutes}m</td><td>{x.ema_fast}/{x.ema_slow}</td><td>{x.rsi_long}/{x.rsi_short}</td><td>{x.adx_min || "—"}</td><td>{x.or_width_min_pct ? `${x.or_width_min_pct}%` : "—"}</td>
                      <td>{x.in_sample.trades}</td><td>{pf(x.in_sample.profit_factor)}</td>
                      <td>{o.trades}</td><td className={o.profit_factor >= 2 ? "up font-semibold" : ""}>{pf(o.profit_factor)}</td>
                      <td>{num(o.win_rate)}</td><td className={pnlClass(o.net_pnl)}>{rupee(o.net_pnl)}</td><td>{rupee(o.max_drawdown)}</td>
                      <td><button data-testid={`button-apply-sweep-${x.rank}`} onClick={() => onApply(x)} className="text-[11px] text-indigo-300 hover:text-indigo-200">Apply</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[11px] text-slate-500">Faded rows have fewer than {res.min_oos_trades} out-of-sample trades. Ranking on one OOS window can overfit, so confirm on a fresh date range.</p>
        </>
      )}
    </div>
  );
};

const F = ({ t, children, w = "w-40" }) => (
  <label className={`block ${w}`}><span className="eyebrow !text-[10px]">{t}</span><div className="mt-1 [&_input]:num [&_input]:h-8 [&_input]:bg-slate-900 [&_input]:text-xs">{children}</div></label>
);
